import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography, Grid, Divider, Button, Chip, useTheme, useMediaQuery } from "@mui/material";
import { FormTextField, Select } from "../controls";
import { checkoutSchema } from "../validation/checkoutSchema";
import OrderTotals from "../components/OrderTotals";
import CustomerAddress from "../components/CustomerAddress";
import { makeRequest } from "../apis/makeRequest";
import { getCustomer, getCustomerAddresses } from "../apis/backendRequests";
import { postOrder } from "../apis/backendRequests";
import useTotals from "../hooks/useTotals";
import { cartActions, notificationActions, spinnerActions } from "../actions";
import PopUpOrder from "../components/PopUpOrder";
import { trackPurchase } from "../helpers/analytics";
import { trackPurchaseMeta } from "../helpers/meta";
import { removeDuplicates } from "../helpers/functions";

const fields = checkoutSchema.describe().fields;
const addressFields = fields.customerAddresses.innerType.fields;

export default function Checkout() {
  const { state } = useLocation();
  const { items, webOrderId } = useSelector((state) => state.cart);
  const { dynamicsId, unique_name, salesData } = useSelector(
    (state) => state.account
  );
  const [delivery, setDelivery] = useState({ facturacion: "", envio: "" });
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [openPreData, setOpenPreData] = useState({ item: {}, open: false });
  const totals = useTotals(removeDuplicates(items), dynamicsId, state);
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down("md"))

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { register, control, formState, handleSubmit, setValue, watch } =
    useForm({
      resolver: yupResolver(checkoutSchema),
    });

  const selectedDelivery = watch("shipmthd");

  useEffect(() => {
    if (!dynamicsId) return;

    makeRequest(
      () => getCustomerAddresses(dynamicsId),
      (res) => setCustomerAddresses(res.data)
    );
  }, [dynamicsId, webOrderId]);

  useEffect(() => {
    if (!dynamicsId) return;

    makeRequest(
      () => getCustomer(dynamicsId),
      (res) =>
        setDelivery({
          facturacion: res.data.billToAddress,
          envio: res.data.sendToAddress,
        })
    );
  }, [dynamicsId, webOrderId]);


  const onSubmit = (data) => {
    const discounts = state?.reduce((unique, item) => {
      if (!unique.find((elem) => elem.productId === item.productId)) {
        unique.push(item.detalle);
      }
      return unique;
    }, []);

    let shimpthdIndex = customerAddresses.findIndex((item, i) => {
      if (!isNaN(data.shipmthd)) {
        return i == data.shipmthd;
      }
      return item.addressCode === data.shipmthd;
    });

    data.cartItems = removeDuplicates(items);
    data.totals = totals;
    data.dynamicsId = dynamicsId;
    data.discounts = discounts;
    data.status = 1
    data.unique_name =
      Object.keys(salesData).length !== 0 ? salesData.unique_name : unique_name;
    data.shipmthd = shimpthdIndex;
    if (webOrderId) data.webOrderId = webOrderId;
    console.log(data)
    dispatch(spinnerActions.show());
    postOrder(data)
      .then((res) => {
        let msg;
        if (webOrderId) {
          trackPurchase(res.data.webOrderId, items, totals.total, totals.tax)
          trackPurchaseMeta(res.data.webOrderId, items, totals.total, totals.tax)
          msg = `Se ha actualizado la orden número ${res.data.webOrderId}`;
        }
        else {
          trackPurchase(res.data.webOrderId, items, totals.total, totals.tax)
          trackPurchaseMeta(res.data.webOrderId, items, totals.total, totals.tax)
          msg = `Se ha creado con éxito la orden número ${res.data.webOrderId}`;
        }
        dispatch(cartActions.clear());
        dispatch(notificationActions.showSuccess({ message: msg }));

        return navigate("/products");
      })
      .catch((err) => {
        console.log(err)
        dispatch(
          notificationActions.showError({
            message: err.message,
          })
        );
      })
      .finally(() => dispatch(spinnerActions.hide()));
  };

  let findAddress = customerAddresses.find((address, i) =>
    typeof selectedDelivery === "number"
      ? i === selectedDelivery
      : address.addressCode === selectedDelivery
  );
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={1}>
        <Grid item xs={mobile ? 12 : 8}>
          <Grid
            container
            direction={mobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems={mobile ? "" : "flex-start"}
            spacing={1}
          >
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom>
                Datos de Facturación
              </Typography>
              <Divider style={{ marginBottom: "1em" }} />
            </Grid>
            <Grid item xs={12}>
              {Object.keys(customerAddresses).length !== 0 && (
                <CustomerAddress
                  index={0}
                  customerAddress={customerAddresses.find(
                    (address) => address.addressCode === delivery.facturacion
                  )}
                  schema={addressFields}
                  inputRef={register}
                  control={control}
                  readOnly={true}
                  addressType={"FACTURACION"}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom>
                Direccion de Entrega
              </Typography>
              <Divider style={{ marginBottom: "1em" }} />
            </Grid>
            <Grid item xs={12}>
              {Object.keys(customerAddresses).length !== 0 && (
                <Select
                  defaultValue={delivery.envio}
                  size="small"
                  style={{ width: "100%" }}
                  inputRef={register}
                  setFormValue={setValue}
                  name="shipmthd"
                  options={customerAddresses}
                  schema={fields?.shipmthd}
                  formErrors={formState.errors?.shipmthd}
                />
              )}
              {selectedDelivery !== "1" &&
                Object.keys(customerAddresses).length !== 0 && (
                  <Grid item xs={12}>
                    <Divider style={{ marginBottom: "1em" }} />
                    {
                      <CustomerAddress
                        control={control}
                        index={1}
                        customerAddress={findAddress}
                        schema={addressFields}
                        readOnly={true}
                        formErrors={formState?.errors}
                        inputRef={register}
                        addressType={"ENVIO"}
                      />
                    }
                  </Grid>
                )}
            </Grid>
            <Grid item xs={12}>
              <FormTextField
                style={{ width: "100%" }}
                size="small"
                inputRef={register}
                name="observaciones"
                schema={fields?.observaciones}
                formErrors={formState.errors?.observaciones}
                control={control}
              />
            </Grid>
            {!mobile && <Grid item xs={12} style={{ paddingBottom: 1, display: "flex" }}>
              <Button fullWidth type="submit" variant="contained" sx={{
                color: "white",
                backgroundColor: "black",
                transition: "all 0.2s ease-in-out",
                '&:hover': {
                  backgroundColor: "black",
                  opacity: "0.8"
                }
              }} >
                Confirmar
              </Button>

            </Grid>}
          </Grid>
        </Grid>
        {!mobile && <Grid item xs={1} />}
        <Grid item xs={mobile ? 12 : 3} mt={mobile && 3} mb={mobile && 3}>
          <Grid
            container
            direction={mobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems={mobile ? "" : "flex-start"}
            spacing={1}
          >
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom>
                Totales
              </Typography>
              <Divider style={{ marginBottom: "1em" }} />
            </Grid>
            <Grid item xs={12}>
              <OrderTotals totalDesc={state} dynamicsId={dynamicsId} />
              <PopUpOrder row={openPreData} setter={setOpenPreData} />
              <Typography
                marginTop={1.5}
                variant="caption"
                display="block"
                gutterBottom
              >
                *La entrega de los productos solicitados se realizará en función
                de la disponibilidad de stock de cada uno de ellos. El precio
                final puede sufrir alteraciones dependiendo de las percepciones
                impositivas que pudieran corresponder. Ante cualquier duda
                consulte con su vendedor asignado o al mail
                ventas@pizzini.com.ar
              </Typography>
              <Typography
                marginTop={1}
                variant="caption"
                display="block"
                gutterBottom
              >
                Entrega sin cargo en CABA y GBA. Las entregas al interior del país son sin cargo hasta el expreso seleccionado por el cliente.
              </Typography>
              <Chip
                label="Vista preliminar del pedido"
                sx={{ marginTop: "5px", boxShadow: "1px 1.5px 1px 0px black", width: "100%", padding: "15px" }}
                onClick={() =>
                  setOpenPreData({ item: {}, open: true, checkoutPop: true })
                }
              />

            </Grid>
          </Grid>
        </Grid>
        {mobile && <Grid item xs={12} style={{ paddingBottom: 1, display: "flex", justifyContent: "center" }}>
          <Button fullWidth type="submit" variant="contained" sx={{
            color: "black",
            backgroundColor: "#B5CD7F",
            '&:hover': {
              backgroundColor: "#8BB85A",
            }
          }} >
            Confirmar
          </Button>

        </Grid>}
      </Grid>
    </form>
  );
}
